import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Modal, Card, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import {showToast as toast} from '../toastManager.js';


function CreateExpGB({ViewedPage, shop_domain, app_url}) {
    ViewedPage(36);

    const [loading, setLoading] = useState(false);
    const [experimentName, setExperimentName] = useState('');
    const [featureFlagName, setFeatureFlagName] = useState('');
    const [landingPageUrl, setLandingPageUrl] = useState('');
    const [urlError, setUrlError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Function to validate URL format
    const validateUrl = (url) => {
        if (!url.trim()) return { isValid: false, error: 'URL is required' };
        
        try {
            // Check if URL has a protocol
            if (!url.match(/^https?:\/\//)) {
                return { isValid: false, error: 'URL must start with http:// or https://' };
            }
            
            // Try to create URL object to validate format
            new URL(url);
            
            // Additional validation for common issues
            if (url.length < 10) {
                return { isValid: false, error: 'URL is too short' };
            }
            
            if (url.length > 2048) {
                return { isValid: false, error: 'URL is too long (max 2048 characters)' };
            }
            
            return { isValid: true, error: '' };
        } catch (error) {
            return { isValid: false, error: 'Invalid URL format' };
        }
    };

    // Function to clean URL by removing parameters and trailing slashes
    const cleanUrl = (url) => {
        if (!url) return '';
        
        try {
            // Create URL object to parse the URL
            const urlObj = new URL(url);
            
            // Get only the origin and pathname (remove query parameters, hash, etc.)
            let cleanUrl = urlObj.origin + urlObj.pathname;
            
            // Remove trailing slash if it exists
            cleanUrl = cleanUrl.replace(/\/$/, '');
            
            return cleanUrl;
        } catch (error) {
            // If URL parsing fails, try to clean manually
            let cleanUrl = url;
            
            // Remove query parameters (everything after ?)
            cleanUrl = cleanUrl.split('?')[0];
            
            // Remove hash (everything after #)
            cleanUrl = cleanUrl.split('#')[0];
            
            // Remove trailing slash
            cleanUrl = cleanUrl.replace(/\/$/, '');
            
            return cleanUrl;
        }
    };

    // Handle URL input change with validation
    const handleUrlChange = (e) => {
        const url = e.target.value;
        setLandingPageUrl(url);
        
        if (url.trim()) {
            const validation = validateUrl(url);
            setUrlError(validation.error);
        } else {
            setUrlError('');
        }
    };

    const handleCreateExperiment = async (e) => {
        e.preventDefault();
        
        // Validate inputs
        if (!experimentName.trim()) {
            setErrorMessage('Experiment name is required');
            return;
        }

        // Validate URL before submission
        const urlValidation = validateUrl(landingPageUrl);
        if (!urlValidation.isValid) {
            setErrorMessage(`Invalid URL: ${urlValidation.error}`);
            return;
        }

        setLoading(true);
        setErrorMessage('');

        try {
            const response = await axios.post(`${app_url}/api/create-experiment-gb`, {
                experimentName: experimentName.trim(),
                featureFlagName: featureFlagName.trim() || experimentName.trim(),
                landingPageUrl: cleanUrl(landingPageUrl.trim())
            }, {withCredentials: true});

            if (response.data.success) {
                setSuccessMessage('Experiment created successfully!');
                setExperimentName('');
                setFeatureFlagName('');
                setLandingPageUrl('');
                setUrlError('');
                toast.success('Experiment created successfully!');
            } else {
                setErrorMessage(response.data.message || 'Failed to create experiment');
                toast.error(response.data.message || 'Failed to create experiment');
            }
        } catch (error) {
            console.error("Error Creating Experiment:", error);
            const errorMsg = error.response?.data?.message || 'Failed to create experiment';
            setErrorMessage(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='main-container'>
            {!shop_domain?
                <div className='go_to_shopify_admin_div'>
                    <h5 style={{display:'inline-block'}}>!! Go to Shopify Admin and open our app !!</h5>
                </div>
                :
                <>
                    { !loading ?
                        <>
                            <div className='main-title mt-4'>
                                <h2>GrowthBook Experiment Setup</h2>
                            </div>

                            <Container className='mt-4'>
                                <Row className="justify-content-center">
                                    <Col md={8} lg={6}>
                                        <Card className="shadow-sm">
                                            <Card.Body className="p-4">
                                                {successMessage && (
                                                    <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
                                                        {successMessage}
                                                    </Alert>
                                                )}
                                                
                                                {errorMessage && (
                                                    <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
                                                        {errorMessage}
                                                    </Alert>
                                                )}

                                                <Form onSubmit={handleCreateExperiment}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            <strong>Experiment Name *</strong>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Experiment Name"
                                                            value={experimentName}
                                                            onChange={(e) => setExperimentName(e.target.value)}
                                                            required
                                                        />
                                                    </Form.Group>

                                                    <Form.Group className="mb-4">
                                                        <Form.Label>
                                                            <strong>Feature Flag Name *</strong>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Feature Flag Name"
                                                            value={featureFlagName}
                                                            onChange={(e) => setFeatureFlagName(e.target.value)}
                                                            required
                                                        />
                                                    </Form.Group>

                                                    <Form.Group className="mb-4">
                                                        <Form.Label>
                                                            <strong>Landing Page URL *</strong>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="url"
                                                            placeholder="https://example.com/landing-page"
                                                            value={landingPageUrl}
                                                            onChange={handleUrlChange}
                                                            isInvalid={!!urlError}
                                                            isValid={!urlError && landingPageUrl.trim()}
                                                            required
                                                        />
                                                        {urlError && (
                                                            <Form.Control.Feedback type="invalid">
                                                                {urlError}
                                                            </Form.Control.Feedback>
                                                        )}
                                                    </Form.Group>

                                                    <div className="d-grid">
                                                        <Button 
                                                            type="submit" 
                                                            variant="primary" 
                                                            size="lg"
                                                            disabled={!experimentName.trim() || !featureFlagName.trim() || !landingPageUrl.trim() || !!urlError}
                                                        >
                                                            Create Experiment
                                                        </Button>
                                                    </div>
                                                </Form>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </Container>
                        </>
                    : 
                        <div className='main_conatiner_spinner'>
                            <Spinner animation="border"/>
                            <p className="mt-3">Creating your experiment...</p>
                        </div>
                    }
                </>
            }
        </div>
    )
}

export default CreateExpGB;